import { expressQuerySelector, expressEventListener } from "../../common/html";
import { compositeDisposable } from "../../utils/disposable";
import { CreateNavigationDesktopFirstLevel } from "../molecules/navigation-desktop-first-level";
import { CreateNavigationDesktopSecondLevel } from "../molecules/navigation-desktop-second-level";
import {
	CreateNavigationDesktopThirdLevel,
	INavigationDesktopThirdLevelSideContainerDeps,
} from "../molecules/navigation-desktop-third-level";

export function CreateNavigationDesktop(container: HTMLElement) {
	const HIDE_CLASS = "u-hide";
	const OVERLAY_THIRD_LEVEL_CLASS = "o-navigation-desktop__overlay--full-width";
	const firstLevel = expressQuerySelector<HTMLElement>(container, ".technical-navigation-first-level");
	const secondLevel = expressQuerySelector<HTMLElement>(container, ".technical-navigation-second-level");
	const thirdLevel = expressQuerySelector<HTMLElement>(container, ".technical-navigation-third-level");
	const navigationOverlay = expressQuerySelector<HTMLElement>(container, ".technical-navigation-overlay");
	const navigationBackdrop = expressQuerySelector<HTMLElement>(container, ".technical-navigation-backdrop");
	const navigationHoverContainer = expressQuerySelector<HTMLElement>(
		container,
		".technical-navigation-hover-container",
	);

	const secondLevelComponent = CreateNavigationDesktopSecondLevel(secondLevel, {
		activatedElement: (el: HTMLElement) => toggleThirdLevelMenu(el),
		getActiveIndex: () => {
			return firstLevelActiveIndex.toString();
		},
	});
	const firstLevelComponent = CreateNavigationDesktopFirstLevel(firstLevel, {
		activatedElement: (el: HTMLElement) => toggleSecondLevelMenu(el),
		getActiveIndex: () => {
			return "";
		}, // 0 level never active
	});

	const thirdLevelComponent = CreateNavigationDesktopThirdLevel(thirdLevel, {
		activatedElement: (el: HTMLElement) => {
			return; // does not use deeper levels
		},
		getActiveIndex: () => {
			return `${firstLevelActiveIndex}.${secondLevelActiveIndex}`;
		},
	});

	let firstLevelActiveIndex = -1;
	let secondLevelActiveIndex = -1;
	const updateHoverableNavigationSection = () => {
		navigationHoverContainer.style.height = `${navigationOverlay.offsetHeight + 20}px`;
		navigationHoverContainer.style.top = `${navigationOverlay.offsetTop}px`;
	};
	const resetHoverableNavigationSection = () => {
		navigationHoverContainer.style.height = "0px";
	};
	const closeMenu = () => {
		firstLevelComponent.close();
		toggleSecondLevelMenu(null);
		toggleThirdLevelMenu(null);
	};
	const toggleSecondLevelMenu = async (el: HTMLElement) => {
		navigationOverlay.classList.toggle(OVERLAY_THIRD_LEVEL_CLASS, false);
		if (!el?.dataset.menuItem || el.dataset.hasSecondLevel === "false") {
			navigationOverlay.style.left = "";
			navigationOverlay.classList.add(HIDE_CLASS);
			secondLevelComponent.close();
			thirdLevelComponent.close();
			firstLevelActiveIndex = -1;
			resetHoverableNavigationSection();
			return;
		}

		firstLevelActiveIndex = parseInt(el.dataset.menuItem, 10);

		const isOpened = await secondLevelComponent.open(el.dataset.menuItem);
		if (!isOpened) return;

		thirdLevelComponent.close();
		navigationOverlay.classList.remove(HIDE_CLASS);
		// subtract left padding applied on the first-level container so the overlay aligns visually
		const containerRect = container.getBoundingClientRect();
		const elRect = el.getBoundingClientRect();
		const firstLevelPaddingLeft = firstLevel ? parseFloat(window.getComputedStyle(firstLevel).paddingLeft) || 0 : 0;
		navigationOverlay.style.left = `${elRect.left - containerRect.left - firstLevelPaddingLeft}px`;
		updateHoverableNavigationSection();
	};

	const toggleThirdLevelMenu = async (el: HTMLElement) => {
		navigationOverlay.classList.toggle(OVERLAY_THIRD_LEVEL_CLASS, el && el.dataset.hasThirdLevel === "true");

		if (!el?.dataset.menuItem || firstLevelActiveIndex < 0 || el.dataset.hasThirdLevel === "false") {
			thirdLevelComponent.close();
			resetHoverableNavigationSection();
			return;
		}

		secondLevelActiveIndex = parseInt(el.dataset.menuItem, 10);
		const sideContainerDeps = {
			title: el.dataset.name,
			link: el.dataset.link,
			imageUrl: el.dataset.imageUrl,
		} as INavigationDesktopThirdLevelSideContainerDeps;

		const isOpened = await thirdLevelComponent.open(
			`${firstLevelActiveIndex}.${secondLevelActiveIndex}`,
			sideContainerDeps,
		);
		if (!isOpened) return;
		updateHoverableNavigationSection();
	};

	const navigationDisposible = compositeDisposable([
		expressEventListener(container, "mouseleave", () => closeMenu()),
		expressEventListener(navigationHoverContainer, "click", () => closeMenu()),
		expressEventListener(navigationBackdrop, "click", () => closeMenu()),
		expressEventListener(navigationOverlay, "mouseleave", () => {
			if (secondLevelActiveIndex < 0) return;

			secondLevelComponent.resetSelected();
		}),
	]);

	return {
		dispose: () => {
			navigationDisposible.dispose();
			firstLevelComponent.dispose();
			secondLevelComponent.dispose();
			thirdLevelComponent.dispose();
		},
	};
}
